import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  RefreshCw,
  Search,
  X,
  Play,
  RotateCcw,
  Power,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  ExternalLink,
  Copy,
  Check,
  Loader2,
  Globe,
  Terminal,
  Pin,
  Edit3,
  Trash2,
  Plus,
  Calendar,
  Layers,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  ShieldAlert,
  Server,
  Zap,
  Info,
  SlidersHorizontal,
} from "lucide-react";

import {
  forumMonitoring,
  type ForumStatus,
  type ForumItem,
  type ForumDetail,
  type RunItem,
  type ErrorItem,
  type NoteItem,
  type ActivityItem,
  type SummaryData,
} from "../../api/forumMonitoring";
import { useHeaderStore } from "../../store/headerStore";
import "../../styles/forum-monitoring.css";

// ─── Constants & Formatting Helpers ──────────────────────────────────────────

const ALL_STATUSES: ForumStatus[] = [
  "SUCCESS",
  "PARTIAL",
  "FAILED",
  "RUNNING",
  "COOLDOWN",
  "BLOCKED",
  "DISABLED",
  "NOT_RUN",
  "STALE",
];

function formatDate(val?: string | null): string {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "—";
  }
}

function timeAgo(val?: string | null): string {
  if (!val) return "Never";
  try {
    const d = new Date(val).getTime();
    if (isNaN(d)) return "Never";
    const sec = Math.max(0, Math.floor((Date.now() - d) / 1000));
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  } catch {
    return "Never";
  }
}

function formatDuration(sec?: number | null): string {
  if (sec == null || isNaN(sec)) return "—";
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const remainingSec = s % 60;
  return `${m}m ${remainingSec}s`;
}

function formatNumber(val?: number | null): string {
  return Number(val || 0).toLocaleString();
}

// ─── Status Badge Component ──────────────────────────────────────────────────

export function ForumStatusBadge({ status }: { status: string }) {
  const norm = (status || "NOT_RUN").toUpperCase();
  const label = norm.replace("_", " ");
  const variantClass = `fmp-badge-${norm.toLowerCase()}`;

  return (
    <span className={`fmp-badge ${variantClass}`}>
      <span className="fmp-badge-dot" />
      <span>{label}</span>
    </span>
  );
}

// ─── Network Badge Component ─────────────────────────────────────────────────

export function NetworkBadge({ network }: { network: string }) {
  const isTor = (network || "").toLowerCase() === "tor";
  return (
    <span className={`fmp-network-badge ${isTor ? "tor" : "clearnet"}`}>
      {isTor ? <ShieldAlert size={12} /> : <Globe size={12} />}
      <span>{isTor ? "Tor (.onion)" : "Clearnet"}</span>
    </span>
  );
}

// ─── Action Buttons Component with Polling Feedback ──────────────────────────

export function ActionButtons({
  forum,
  enabled,
  onActionComplete,
}: {
  forum: string;
  enabled: boolean;
  onActionComplete?: () => void;
}) {
  const [runningAction, setRunningAction] = useState<string | null>(null);
  const [activeRequest, setActiveRequest] = useState<{
    request_id?: string;
    action?: string;
    status?: string;
    message?: string;
  } | null>(null);

  const handleAction = async (action: "RUN_NOW" | "CLEAR_COOLDOWN" | "ENABLE" | "DISABLE") => {
    if (action === "DISABLE" && !window.confirm(`Are you sure you want to disable forum scraper for '${forum}'?`)) {
      return;
    }

    setRunningAction(action);
    try {
      const res = await forumMonitoring.action(forum, action);
      setActiveRequest({
        request_id: res.request_id,
        action,
        status: res.status,
      });
      toast.success(`${action.replace("_", " ")} queued for ${forum}`);
      onActionComplete?.();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || `Failed to trigger ${action}`);
      setRunningAction(null);
    }
  };

  // Poll status of active action
  useEffect(() => {
    if (!activeRequest?.request_id) return;
    if (["SUCCESS", "FAILED"].includes(activeRequest.status || "")) {
      setRunningAction(null);
      return;
    }

    const timer = setInterval(async () => {
      try {
        const update = await forumMonitoring.actionStatus(activeRequest.request_id!);
        if (update) {
          setActiveRequest(update);
          if (["SUCCESS", "FAILED"].includes(update.status)) {
            setRunningAction(null);
            if (update.status === "SUCCESS") {
              toast.success(`${update.action} completed successfully`);
            } else {
              toast.error(`${update.action} failed: ${update.message || "Unknown error"}`);
            }
            onActionComplete?.();
          }
        }
      } catch {
        // retain last known status
      }
    }, 2000);

    return () => clearInterval(timer);
  }, [activeRequest?.request_id, activeRequest?.status, onActionComplete]);

  return (
    <div className="fmp-action-group">
      <button
        type="button"
        className="fmp-action-btn run"
        onClick={() => handleAction("RUN_NOW")}
        disabled={!!runningAction}
        title="Trigger immediate crawler job run"
      >
        {runningAction === "RUN_NOW" ? (
          <Loader2 size={13} className="animate-spin" />
        ) : (
          <Play size={13} />
        )}
        <span>Run now</span>
      </button>

      <button
        type="button"
        className="fmp-action-btn"
        onClick={() => handleAction("CLEAR_COOLDOWN")}
        disabled={!!runningAction}
        title="Reset transient backoff & cooldown"
      >
        {runningAction === "CLEAR_COOLDOWN" ? (
          <Loader2 size={13} className="animate-spin" />
        ) : (
          <RotateCcw size={13} />
        )}
        <span>Clear cooldown</span>
      </button>

      <button
        type="button"
        className={`fmp-action-btn ${enabled ? "danger" : ""}`}
        onClick={() => handleAction(enabled ? "DISABLE" : "ENABLE")}
        disabled={!!runningAction}
        title={enabled ? "Disable this scraper" : "Enable this scraper"}
      >
        {runningAction === (enabled ? "DISABLE" : "ENABLE") ? (
          <Loader2 size={13} className="animate-spin" />
        ) : (
          <Power size={13} />
        )}
        <span>{enabled ? "Disable" : "Enable"}</span>
      </button>

      {activeRequest && (
        <span
          style={{
            fontSize: "0.74rem",
            color: activeRequest.status === "SUCCESS" ? "#34d399" : activeRequest.status === "FAILED" ? "#f87171" : "#818cf8",
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          {activeRequest.status === "PENDING" && <Loader2 size={11} className="animate-spin" />}
          {activeRequest.action}: {activeRequest.status}
        </span>
      )}
    </div>
  );
}

// ─── Run Telemetry Modal ──────────────────────────────────────────────────────

function RunTelemetryModal({
  run,
  onClose,
}: {
  run: RunItem | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  if (!run) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(run, null, 2));
    setCopied(true);
    toast.success("Telemetry JSON copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fmp-modal-overlay" onClick={onClose}>
      <div className="fmp-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="fmp-modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Terminal size={18} style={{ color: "#818cf8" }} />
            <h3 style={{ margin: 0, fontSize: "1.05rem", color: "#ffffff", fontWeight: 600 }}>
              Run Telemetry: <code style={{ fontSize: "0.9rem", color: "#a5b4fc" }}>{run.run_id}</code>
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer" }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="fmp-modal-body">
          {/* Key Metrics Summary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px" }}>
            <div className="fmp-stat-card" style={{ padding: "10px 14px" }}>
              <span className="fmp-stat-top">STATUS</span>
              <div style={{ marginTop: "4px" }}>
                <ForumStatusBadge status={run.status || run.run_status || "UNKNOWN"} />
              </div>
            </div>
            <div className="fmp-stat-card" style={{ padding: "10px 14px" }}>
              <span className="fmp-stat-top">DURATION</span>
              <strong style={{ fontSize: "1.1rem", color: "#ffffff", marginTop: "4px" }}>
                {formatDuration(run.duration_seconds)}
              </strong>
            </div>
            <div className="fmp-stat-card" style={{ padding: "10px 14px" }}>
              <span className="fmp-stat-top">JOBS (SUCC / TOTAL)</span>
              <strong style={{ fontSize: "1.1rem", color: "#ffffff", marginTop: "4px" }}>
                {run.jobs_succeeded || 0} / {run.jobs_queued || 0}
              </strong>
            </div>
            <div className="fmp-stat-card" style={{ padding: "10px 14px" }}>
              <span className="fmp-stat-top">NEW LEAKS</span>
              <strong style={{ fontSize: "1.1rem", color: (run.new_records || 0) > 0 ? "#34d399" : "#ffffff", marginTop: "4px" }}>
                {run.new_records || 0}
              </strong>
            </div>
          </div>

          {/* Failure message if any */}
          {run.failure_message && (
            <div
              style={{
                padding: "14px 16px",
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                borderRadius: "8px",
                color: "#fee2e2",
                fontSize: "0.86rem",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px", fontWeight: 600 }}>
                <AlertTriangle size={15} style={{ color: "#ef4444" }} />
                <span>Failure Reason: {run.failure_type || "scraper_error"}</span>
              </div>
              <p style={{ margin: 0, lineHeight: 1.5, color: "#fca5a5" }}>{run.failure_message}</p>
            </div>
          )}

          {/* Raw JSON viewer */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "6px" }}>
            <span style={{ fontSize: "0.8rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>
              Raw Run Data
            </span>
            <button type="button" className="fmp-btn" onClick={handleCopy} style={{ padding: "4px 10px", fontSize: "0.76rem" }}>
              {copied ? <Check size={13} /> : <Copy size={13} />}
              <span>{copied ? "Copied" : "Copy JSON"}</span>
            </button>
          </div>

          <pre className="fmp-code-viewer">{JSON.stringify(run, null, 2)}</pre>
        </div>
      </div>
    </div>
  );
}

// ─── Diagnostic Error Modal ───────────────────────────────────────────────────

function DiagnosticErrorModal({
  error,
  onClose,
  onResolve,
}: {
  error: ErrorItem | null;
  onClose: () => void;
  onResolve: (status: "ACKNOWLEDGED" | "RESOLVED", note?: string) => Promise<void>;
}) {
  const [copied, setCopied] = useState(false);
  const [resolving, setResolving] = useState(false);

  if (!error) return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(error, null, 2));
    setCopied(true);
    toast.success("Error diagnostic copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleResolveAction = async (status: "ACKNOWLEDGED" | "RESOLVED") => {
    let note = "";
    if (status === "RESOLVED") {
      const res = window.prompt("Optional resolution note / comment:");
      note = res || "";
    }
    setResolving(true);
    try {
      await onResolve(status, note);
      onClose();
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="fmp-modal-overlay" onClick={onClose}>
      <div className="fmp-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="fmp-modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <AlertTriangle size={18} style={{ color: "#ef4444" }} />
            <h3 style={{ margin: 0, fontSize: "1.05rem", color: "#ffffff", fontWeight: 600 }}>
              Diagnostic: <span style={{ color: "#fca5a5" }}>{error.error_type}</span>
            </h3>
          </div>
          <button
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer" }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="fmp-modal-body">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
            <div>
              <p style={{ margin: "0 0 4px 0", fontSize: "0.95rem", color: "#f8fafc", fontWeight: 500 }}>
                {error.message}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "0.78rem", color: "#94a3b8" }}>
                <span>ID: {error.error_id}</span>
                <span>•</span>
                <span>Last seen: {formatDate(error.last_seen)}</span>
                {error.resolution_status && (
                  <>
                    <span>•</span>
                    <span style={{ color: error.resolution_status === "RESOLVED" ? "#34d399" : "#f59e0b", fontWeight: 600 }}>
                      {error.resolution_status}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                type="button"
                className="fmp-btn"
                onClick={() => handleResolveAction("ACKNOWLEDGED")}
                disabled={resolving || error.resolution_status === "ACKNOWLEDGED"}
              >
                Acknowledge
              </button>
              <button
                type="button"
                className="fmp-btn fmp-btn-primary"
                onClick={() => handleResolveAction("RESOLVED")}
                disabled={resolving || error.resolution_status === "RESOLVED"}
              >
                Resolve
              </button>
            </div>
          </div>

          {error.stack_trace && (
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ fontSize: "0.78rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>
                  Stack Trace
                </span>
                <button type="button" className="fmp-btn" onClick={handleCopy} style={{ padding: "4px 10px", fontSize: "0.76rem" }}>
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  <span>{copied ? "Copied" : "Copy"}</span>
                </button>
              </div>
              <pre className="fmp-code-viewer">{error.stack_trace}</pre>
            </div>
          )}

          {!error.stack_trace && (
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ fontSize: "0.78rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase" }}>
                  Diagnostic Context
                </span>
                <button type="button" className="fmp-btn" onClick={handleCopy} style={{ padding: "4px 10px", fontSize: "0.76rem" }}>
                  {copied ? <Check size={13} /> : <Copy size={13} />}
                  <span>{copied ? "Copied" : "Copy JSON"}</span>
                </button>
              </div>
              <pre className="fmp-code-viewer">{JSON.stringify(error, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE: FORUM MONITORING FLEET VIEW
// ═══════════════════════════════════════════════════════════════════════════════

export default function ForumMonitoringPage() {
  const { setPageTitleOverride } = useHeaderStore();

  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [data, setData] = useState<{
    items: ForumItem[];
    total: number;
    page: number;
    page_size: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>("");
  const [network, setNetwork] = useState<string>("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [sort, setSort] = useState("forum");
  const [direction, setDirection] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(30); // in seconds, 0 = off

  useEffect(() => {
    setPageTitleOverride("FORUM MONITORING");
    return () => setPageTitleOverride(null);
  }, [setPageTitleOverride]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: pageSize,
        search: search.trim() || undefined,
        status: status || undefined,
        network: network || undefined,
        has_errors: errorsOnly || undefined,
        sort,
        direction,
      };

      const [summaryRes, forumsRes] = await Promise.all([
        forumMonitoring.summary(),
        forumMonitoring.forums(params),
      ]);

      setSummary(summaryRes);
      setData(forumsRes);
    } catch (err: any) {
      console.error("Failed to load forum monitoring data:", err);
      toast.error("Forum monitoring service is temporarily unavailable");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, status, network, errorsOnly, sort, direction]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh interval
  useEffect(() => {
    if (autoRefreshInterval <= 0) return;
    const timer = setInterval(() => {
      loadData();
    }, autoRefreshInterval * 1000);
    return () => clearInterval(timer);
  }, [autoRefreshInterval, loadData]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadData();
  };

  const handleResetFilters = () => {
    setSearch("");
    setStatus("");
    setNetwork("");
    setErrorsOnly(false);
    setSort("forum");
    setDirection("asc");
    setPage(1);
  };

  const activeFiltersCount =
    (search ? 1 : 0) + (status ? 1 : 0) + (network ? 1 : 0) + (errorsOnly ? 1 : 0);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <main className="fmp-container">
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <div className="fmp-header">
        <div className="fmp-header-left">
          <div className="fmp-title-row">
            <h1 className="fmp-title">
              <Server size={24} style={{ color: "#6366f1" }} />
              <span>Forum Monitoring</span>
            </h1>
            <span className="fmp-title-badge">
              {summary ? `${summary.total_forums} Scrapers Monitored` : "Fleet"}
            </span>
          </div>
          <p className="fmp-subtitle">
            Registry-discovered operational telemetry & crawler health. Queued attempts are never treated as success.
          </p>
        </div>

        <div className="fmp-header-actions">
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Clock size={14} style={{ color: "#64748b" }} />
            <select
              className="fmp-auto-refresh-select"
              value={autoRefreshInterval}
              onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
              title="Auto-refresh frequency"
            >
              <option value={0}>Auto-refresh: Off</option>
              <option value={10}>Every 10s</option>
              <option value={30}>Every 30s</option>
              <option value={60}>Every 60s</option>
            </select>
          </div>

          <button
            type="button"
            className="fmp-btn fmp-btn-primary"
            onClick={loadData}
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* ─── KPI & Status Metrics ────────────────────────────────────────── */}
      <div className="fmp-metrics-container">
        {/* Core Fleet Numbers */}
        <div className="fmp-overview-grid">
          <div className="fmp-stat-card primary">
            <div className="fmp-stat-top">
              <span>Total Fleet</span>
              <Layers size={16} />
            </div>
            <div className="fmp-stat-val-row">
              <span className="fmp-stat-val">{summary ? formatNumber(summary.total_forums) : "—"}</span>
              <span className="fmp-stat-sub">configured scrapers</span>
            </div>
          </div>

          <div className="fmp-stat-card success">
            <div className="fmp-stat-top">
              <span>Operational Success</span>
              <CheckCircle2 size={16} style={{ color: "#10b981" }} />
            </div>
            <div className="fmp-stat-val-row">
              <span className="fmp-stat-val" style={{ color: "#34d399" }}>
                {summary ? formatNumber(summary.status_counts["SUCCESS"] || 0) : "—"}
              </span>
              <span className="fmp-stat-sub">
                {summary && summary.total_forums > 0
                  ? `${Math.round(((summary.status_counts["SUCCESS"] || 0) / summary.total_forums) * 100)}% of fleet`
                  : ""}
              </span>
            </div>
          </div>

          <div className="fmp-stat-card danger">
            <div className="fmp-stat-top">
              <span>Failed / Errors</span>
              <AlertTriangle size={16} style={{ color: "#ef4444" }} />
            </div>
            <div className="fmp-stat-val-row">
              <span className="fmp-stat-val" style={{ color: "#f87171" }}>
                {summary ? formatNumber(summary.status_counts["FAILED"] || 0) : "—"}
              </span>
              <span className="fmp-stat-sub">
                {summary ? `${formatNumber(summary.unresolved_errors)} unresolved` : ""}
              </span>
            </div>
          </div>

          <div className="fmp-stat-card">
            <div className="fmp-stat-top">
              <span>24h Runs Activity</span>
              <Activity size={16} style={{ color: "#818cf8" }} />
            </div>
            <div className="fmp-stat-val-row">
              <span className="fmp-stat-val" style={{ color: "#a5b4fc" }}>
                {summary ? formatNumber(summary.runs_today) : "—"}
              </span>
              <span className="fmp-stat-sub">
                {summary ? `${formatNumber(summary.errors_today)} errors today` : ""}
              </span>
            </div>
          </div>
        </div>

        {/* Interactive Clickable Status Filter Pills */}
        <div className="fmp-status-pills-grid">
          {ALL_STATUSES.map((st) => {
            const count = summary?.status_counts[st] || 0;
            const isActive = status === st;
            return (
              <button
                key={st}
                type="button"
                className={`fmp-status-card-btn ${isActive ? "active" : ""}`}
                onClick={() => {
                  setStatus(isActive ? "" : st);
                  setPage(1);
                }}
              >
                <span className="fmp-status-card-label">
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background:
                        st === "SUCCESS"
                          ? "#10b981"
                          : st === "FAILED"
                          ? "#ef4444"
                          : st === "PARTIAL"
                          ? "#f59e0b"
                          : st === "RUNNING"
                          ? "#3b82f6"
                          : "#64748b",
                    }}
                  />
                  {st.replace("_", " ")}
                </span>
                <span className="fmp-status-card-count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Filter & Search Toolbar ──────────────────────────────────────── */}
      <form className="fmp-toolbar" onSubmit={handleSearchSubmit}>
        <div className="fmp-toolbar-left">
          <div className="fmp-search-box">
            <Search size={15} className="fmp-search-icon" />
            <input
              type="text"
              className="fmp-search-input"
              placeholder="Search by forum name or plugin..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="fmp-search-clear"
                onClick={() => {
                  setSearch("");
                  setPage(1);
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <select
            className="fmp-select"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Statuses</option>
            {ALL_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replace("_", " ")}
              </option>
            ))}
          </select>

          <select
            className="fmp-select"
            value={network}
            onChange={(e) => {
              setNetwork(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Networks</option>
            <option value="tor">Tor (.onion)</option>
            <option value="clearnet">Clearnet</option>
          </select>

          <select
            className="fmp-select"
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              setPage(1);
            }}
          >
            <option value="forum">Sort by: Forum Name</option>
            <option value="status">Sort by: Status</option>
            <option value="last_run">Sort by: Last Run</option>
            <option value="last_success">Sort by: Last Success</option>
            <option value="duration">Sort by: Duration</option>
            <option value="errors">Sort by: Errors Count</option>
            <option value="new_leaks">Sort by: New Leaks</option>
          </select>

          <button
            type="button"
            className="fmp-btn"
            style={{ padding: "8px 12px" }}
            onClick={() => setDirection(direction === "asc" ? "desc" : "asc")}
            title="Toggle sort direction"
          >
            <SlidersHorizontal size={13} />
            <span>{direction.toUpperCase()}</span>
          </button>

          <label className={`fmp-checkbox-pill ${errorsOnly ? "active" : ""}`}>
            <input
              type="checkbox"
              checked={errorsOnly}
              onChange={(e) => {
                setErrorsOnly(e.target.checked);
                setPage(1);
              }}
            />
            <AlertTriangle size={13} />
            <span>Errors Only</span>
          </label>
        </div>

        <div className="fmp-toolbar-right">
          {activeFiltersCount > 0 && (
            <button type="button" className="fmp-reset-btn" onClick={handleResetFilters}>
              Reset filters ({activeFiltersCount})
            </button>
          )}

          <button type="submit" className="fmp-btn fmp-btn-primary">
            <span>Apply</span>
          </button>
        </div>
      </form>

      {/* ─── Forums Data Table ────────────────────────────────────────────── */}
      <div className="fmp-table-card">
        <div className="fmp-table-responsive">
          <table className="fmp-table">
            <thead>
              <tr>
                <th>Forum & Plugin</th>
                <th>Network</th>
                <th>Status</th>
                <th>Last Run</th>
                <th>Last Success</th>
                <th>Duration</th>
                <th>Pages</th>
                <th>Jobs Queue</th>
                <th>New Leaks</th>
                <th>Errors</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr>
                  <td colSpan={11} style={{ textAlign: "center", padding: "48px 0", color: "#94a3b8" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                      <Loader2 size={20} className="animate-spin text-indigo-400" />
                      <span>Loading fleet telemetry...</span>
                    </div>
                  </td>
                </tr>
              ) : data?.items?.length ? (
                data.items.map((row) => (
                  <tr key={row.forum}>
                    <td>
                      <div className="fmp-forum-name-col">
                        <Link
                          to={`/admin/forum-monitoring/${encodeURIComponent(row.forum)}`}
                          className="fmp-forum-link"
                        >
                          <span>{row.forum}</span>
                          <ChevronRight size={14} />
                        </Link>
                        <span className="fmp-plugin-tag">{row.plugin}</span>
                      </div>
                    </td>

                    <td>
                      <NetworkBadge network={row.network} />
                    </td>

                    <td>
                      <ForumStatusBadge status={row.status} />
                    </td>

                    <td>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontSize: "0.82rem", color: "#e2e8f0" }}>{formatDate(row.last_run)}</span>
                        <span style={{ fontSize: "0.72rem", color: "#64748b" }}>{timeAgo(row.last_run)}</span>
                      </div>
                    </td>

                    <td>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontSize: "0.82rem", color: "#e2e8f0" }}>{formatDate(row.last_success)}</span>
                        <span style={{ fontSize: "0.72rem", color: "#64748b" }}>{timeAgo(row.last_success)}</span>
                      </div>
                    </td>

                    <td>
                      <span className="fmp-pill-metric">
                        {formatDuration(row.duration)}
                      </span>
                    </td>

                    <td>
                      <span style={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
                        {formatNumber(row.pages)}
                      </span>
                    </td>

                    <td>
                      <span style={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
                        {formatNumber(row.jobs_succeeded)} / {formatNumber(row.jobs)}
                      </span>
                    </td>

                    <td>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px" }}>
                        <span
                          className={`fmp-pill-metric ${(row.new_leaks || 0) > 0 ? "has-value" : ""}`}
                          title={
                            row.new_leaks_24h !== undefined
                              ? `${formatNumber(row.new_leaks_24h)} new leaks in last 24h`
                              : undefined
                          }
                        >
                          {formatNumber(row.new_leaks)}
                        </span>
                        {row.total_leaks !== undefined && row.total_leaks > 0 && (
                          <span style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 500 }}>
                            {formatNumber(row.total_leaks)} stored
                          </span>
                        )}
                      </div>
                    </td>

                    <td>
                      <span className={`fmp-pill-metric ${(row.errors || 0) > 0 ? "has-errors" : ""}`}>
                        {formatNumber(row.errors)}
                      </span>
                    </td>

                    <td style={{ textAlign: "right" }}>
                      <ActionButtons
                        forum={row.forum}
                        enabled={row.enabled}
                        onActionComplete={loadData}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={11} style={{ textAlign: "center", padding: "48px 0", color: "#94a3b8" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                      <AlertTriangle size={24} style={{ color: "#f59e0b" }} />
                      <span style={{ fontWeight: 500 }}>No forums match the selected filters</span>
                      <button type="button" className="fmp-btn" onClick={handleResetFilters}>
                        Clear all filters
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ─── Pagination Footer ───────────────────────────────────────────── */}
        {data && (
          <div className="fmp-pagination">
            <div className="fmp-pagination-info">
              Showing{" "}
              <strong>
                {data.total === 0 ? 0 : (page - 1) * data.page_size + 1}–
                {Math.min(data.total, page * data.page_size)}
              </strong>{" "}
              of <strong>{formatNumber(data.total)}</strong> forums
            </div>

            <div className="fmp-pagination-controls">
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginRight: "12px" }}>
                <span style={{ fontSize: "0.78rem", color: "#64748b" }}>Per page:</span>
                <select
                  className="fmp-select"
                  style={{ padding: "4px 8px", fontSize: "0.78rem" }}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <button
                type="button"
                className="fmp-page-btn"
                onClick={() => setPage(1)}
                disabled={page <= 1}
                title="First Page"
              >
                <ChevronsLeft size={14} />
              </button>

              <button
                type="button"
                className="fmp-page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                title="Previous Page"
              >
                <ChevronLeft size={14} />
              </button>

              <span style={{ fontSize: "0.8rem", color: "#cbd5e1", padding: "0 8px" }}>
                Page <strong>{page}</strong> of <strong>{totalPages}</strong>
              </span>

              <button
                type="button"
                className="fmp-page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                title="Next Page"
              >
                <ChevronRight size={14} />
              </button>

              <button
                type="button"
                className="fmp-page-btn"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                title="Last Page"
              >
                <ChevronsRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DETAIL PAGE: INDIVIDUAL FORUM DASHBOARD & TABS
// ═══════════════════════════════════════════════════════════════════════════════

export function ForumMonitoringDetailPage() {
  const { forum = "" } = useParams();
  const navigate = useNavigate();
  const { setPageTitleOverride } = useHeaderStore();

  const [tab, setTab] = useState<"Overview" | "Runs" | "Errors" | "Notes" | "Activity">("Overview");
  const [detail, setDetail] = useState<ForumDetail | null>(null);
  const [loading, setLoading] = useState(true);

  // Tab data items
  const [runs, setRuns] = useState<RunItem[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsFilter, setRunsFilter] = useState("ALL");

  const [errors, setErrors] = useState<ErrorItem[]>([]);
  const [errorsLoading, setErrorsLoading] = useState(false);

  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [newNoteText, setNewNoteText] = useState("");
  const [pinNewNote, setPinNewNote] = useState(false);
  const [submittingNote, setSubmittingNote] = useState(false);

  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [activityFilter, setActivityFilter] = useState("ALL");

  // Modals
  const [selectedRun, setSelectedRun] = useState<RunItem | null>(null);
  const [selectedError, setSelectedError] = useState<ErrorItem | null>(null);

  // Base URL copy
  const [copiedUrl, setCopiedUrl] = useState(false);

  useEffect(() => {
    setPageTitleOverride(`FORUM: ${forum.toUpperCase()}`);
    return () => setPageTitleOverride(null);
  }, [forum, setPageTitleOverride]);

  const loadForumDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res = await forumMonitoring.forum(forum);
      setDetail(res);
    } catch (err: any) {
      toast.error(`Unable to load details for forum '${forum}'`);
    } finally {
      setLoading(false);
    }
  }, [forum]);

  const loadTabData = useCallback(async () => {
    if (!forum) return;
    try {
      if (tab === "Runs") {
        setRunsLoading(true);
        const res = await forumMonitoring.runs(forum, { page: 1, page_size: 50 });
        setRuns(res.items || []);
        setRunsLoading(false);
      } else if (tab === "Errors") {
        setErrorsLoading(true);
        const res = await forumMonitoring.errors(forum, { page: 1, page_size: 50 });
        setErrors(res.items || []);
        setErrorsLoading(false);
      } else if (tab === "Notes") {
        const res = await forumMonitoring.notes(forum);
        setNotes(res || []);
      } else if (tab === "Activity") {
        const res = await forumMonitoring.activity(forum, { page: 1, page_size: 50 });
        setActivities(res.items || []);
      }
    } catch (err: any) {
      console.error(`Error loading data for tab ${tab}:`, err);
    }
  }, [forum, tab]);

  useEffect(() => {
    loadForumDetail();
  }, [loadForumDetail]);

  useEffect(() => {
    loadTabData();
  }, [loadTabData]);

  const handleCopyBaseUrl = async () => {
    if (!detail?.base_url) return;
    await navigator.clipboard.writeText(detail.base_url);
    setCopiedUrl(true);
    toast.success("Base URL copied");
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleAddNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNoteText.trim()) return;
    setSubmittingNote(true);
    try {
      await forumMonitoring.addNote(forum, newNoteText.trim(), pinNewNote);
      toast.success("Operational note added");
      setNewNoteText("");
      setPinNewNote(false);
      loadTabData();
    } catch (err: any) {
      toast.error("Failed to add note");
    } finally {
      setSubmittingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;
    try {
      await forumMonitoring.deleteNote(forum, noteId);
      toast.success("Note deleted");
      loadTabData();
    } catch {
      toast.error("Failed to delete note");
    }
  };

  const handleTogglePinNote = async (note: NoteItem) => {
    try {
      await forumMonitoring.updateNote(forum, note._id, note.text, !note.pinned);
      toast.success(note.pinned ? "Note unpinned" : "Note pinned to top");
      loadTabData();
    } catch {
      toast.error("Failed to update note pin");
    }
  };

  const handleEditNote = async (note: NoteItem) => {
    const newText = window.prompt("Edit operational note text:", note.text);
    if (newText != null && newText.trim() && newText !== note.text) {
      try {
        await forumMonitoring.updateNote(forum, note._id, newText.trim(), note.pinned);
        toast.success("Note updated");
        loadTabData();
      } catch {
        toast.error("Failed to edit note");
      }
    }
  };

  const handleResolveError = async (status: "ACKNOWLEDGED" | "RESOLVED", resolutionNote = "") => {
    if (!selectedError) return;
    try {
      await forumMonitoring.resolveError(selectedError.error_id, status, resolutionNote);
      toast.success(`Error ${status.toLowerCase()} successfully`);
      loadTabData();
    } catch {
      toast.error("Failed to update error status");
    }
  };

  // Filtered runs
  const filteredRuns = useMemo(() => {
    if (runsFilter === "ALL") return runs;
    return runs.filter((r) => (r.status || r.run_status || "").toUpperCase() === runsFilter);
  }, [runs, runsFilter]);

  // Derived runs that failed (for failure visibility)
  const failedRunsFromHistory = useMemo(() => {
    return runs.filter(
      (r) =>
        (r.status || "").toUpperCase() === "FAILED" ||
        (r.run_status || "").toUpperCase() === "FAILED" ||
        !!r.failure_message
    );
  }, [runs]);

  // Filtered activities
  const filteredActivities = useMemo(() => {
    if (activityFilter === "ALL") return activities;
    return activities.filter((a) => a.type.toLowerCase().includes(activityFilter.toLowerCase()));
  }, [activities, activityFilter]);

  return (
    <main className="fmp-container">
      {/* ─── Back Link ────────────────────────────────────────────────────── */}
      <Link to="/admin/forum-monitoring" className="fmp-back-btn">
        <ArrowLeft size={16} />
        <span>Back to Forum Monitoring Fleet</span>
      </Link>

      {/* ─── Detail Hero Banner ───────────────────────────────────────────── */}
      {loading && !detail ? (
        <div className="fmp-detail-hero">
          <div style={{ display: "flex", alignItems: "center", gap: "10px", color: "#94a3b8" }}>
            <Loader2 size={20} className="animate-spin text-indigo-400" />
            <span>Loading forum telemetry...</span>
          </div>
        </div>
      ) : detail ? (
        <div className="fmp-detail-hero">
          <div className="fmp-detail-title-group">
            <h1 className="fmp-detail-title">
              <span>{detail.forum}</span>
              <ForumStatusBadge status={detail.status} />
            </h1>

            <div className="fmp-detail-meta-row">
              <NetworkBadge network={detail.network} />
              <span>•</span>
              <span style={{ fontFamily: "monospace", color: "#cbd5e1" }}>Plugin: {detail.plugin}</span>
              {detail.base_url && (
                <>
                  <span>•</span>
                  <button
                    type="button"
                    onClick={handleCopyBaseUrl}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#818cf8",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "0.82rem",
                    }}
                    title="Click to copy base URL"
                  >
                    {copiedUrl ? <Check size={13} /> : <Copy size={13} />}
                    <span style={{ textDecoration: "underline" }}>
                      {detail.base_url.length > 40 ? `${detail.base_url.slice(0, 37)}...` : detail.base_url}
                    </span>
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="fmp-detail-actions">
            <ActionButtons
              forum={detail.forum}
              enabled={detail.enabled}
              onActionComplete={() => {
                loadForumDetail();
                loadTabData();
              }}
            />
          </div>
        </div>
      ) : null}

      {/* ─── Tab Bar Navigation ───────────────────────────────────────────── */}
      <div className="fmp-tab-bar">
        <button
          type="button"
          className={`fmp-tab-btn ${tab === "Overview" ? "active" : ""}`}
          onClick={() => setTab("Overview")}
        >
          <Activity size={16} />
          <span>Overview</span>
        </button>

        <button
          type="button"
          className={`fmp-tab-btn ${tab === "Runs" ? "active" : ""}`}
          onClick={() => setTab("Runs")}
        >
          <Play size={16} />
          <span>Runs</span>
          <span className="fmp-tab-badge">
            {detail?.runs?.length || runs.length || "0"}
          </span>
        </button>

        <button
          type="button"
          className={`fmp-tab-btn ${tab === "Errors" ? "active" : ""}`}
          onClick={() => setTab("Errors")}
        >
          <AlertTriangle size={16} />
          <span>Errors & Diagnostics</span>
          {(detail?.health?.unresolved_errors || 0) > 0 && (
            <span className="fmp-tab-badge" style={{ background: "rgba(239, 68, 68, 0.2)", color: "#f87171" }}>
              {detail?.health?.unresolved_errors}
            </span>
          )}
        </button>

        <button
          type="button"
          className={`fmp-tab-btn ${tab === "Notes" ? "active" : ""}`}
          onClick={() => setTab("Notes")}
        >
          <FileText size={16} />
          <span>Operational Notes</span>
          {notes.length > 0 && <span className="fmp-tab-badge">{notes.length}</span>}
        </button>

        <button
          type="button"
          className={`fmp-tab-btn ${tab === "Activity" ? "active" : ""}`}
          onClick={() => setTab("Activity")}
        >
          <Clock size={16} />
          <span>Activity Stream</span>
        </button>
      </div>

      {/* ─── TAB 1: OVERVIEW ──────────────────────────────────────────────── */}
      {tab === "Overview" && detail && (
        <div className="fmp-overview-layout">
          {/* Left Column: Health, Durations & Recent Runs */}
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <div className="fmp-card">
              <h3 className="fmp-card-title">
                <Activity size={18} style={{ color: "#6366f1" }} />
                <span>Crawler Health & Operational Reliability</span>
              </h3>

              <div className="fmp-gauge-row">
                <div
                  className="fmp-gauge-box"
                  style={{
                    borderColor:
                      (detail.health?.success_rate || 0) >= 80
                        ? "#10b981"
                        : (detail.health?.success_rate || 0) >= 50
                        ? "#f59e0b"
                        : "#ef4444",
                    boxShadow:
                      (detail.health?.success_rate || 0) >= 80
                        ? "0 0 16px rgba(16, 185, 129, 0.25)"
                        : "0 0 16px rgba(239, 68, 68, 0.25)",
                  }}
                >
                  <span className="fmp-gauge-val">{detail.health?.success_rate ?? 0}%</span>
                  <span className="fmp-gauge-lbl">Success</span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", flex: 1 }}>
                  <div>
                    <span style={{ fontSize: "0.74rem", color: "#94a3b8", textTransform: "uppercase" }}>
                      Successful Runs
                    </span>
                    <h4 style={{ margin: "4px 0 0", fontSize: "1.3rem", color: "#34d399", fontWeight: 700 }}>
                      {formatNumber(detail.health?.success_runs)}
                    </h4>
                  </div>

                  <div>
                    <span style={{ fontSize: "0.74rem", color: "#94a3b8", textTransform: "uppercase" }}>
                      Partial Runs
                    </span>
                    <h4 style={{ margin: "4px 0 0", fontSize: "1.3rem", color: "#fcd34d", fontWeight: 700 }}>
                      {formatNumber(detail.health?.partial_runs)}
                    </h4>
                  </div>

                  <div>
                    <span style={{ fontSize: "0.74rem", color: "#94a3b8", textTransform: "uppercase" }}>
                      Failed Runs
                    </span>
                    <h4 style={{ margin: "4px 0 0", fontSize: "1.3rem", color: "#f87171", fontWeight: 700 }}>
                      {formatNumber(detail.health?.failed_runs)}
                    </h4>
                  </div>
                </div>
              </div>

              {/* Performance Metrics */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: "12px",
                  marginTop: "8px",
                  paddingTop: "16px",
                  borderTop: "1px solid rgba(255, 255, 255, 0.06)",
                }}
              >
                <div>
                  <span style={{ fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase" }}>
                    Avg Duration
                  </span>
                  <p style={{ margin: "2px 0 0", fontSize: "1rem", color: "#ffffff", fontWeight: 600 }}>
                    {formatDuration(detail.health?.average_duration_seconds)}
                  </p>
                </div>

                <div>
                  <span style={{ fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase" }}>
                    Avg Jobs / Run
                  </span>
                  <p style={{ margin: "2px 0 0", fontSize: "1rem", color: "#ffffff", fontWeight: 600 }}>
                    {detail.health?.average_jobs_per_run ?? 0}
                  </p>
                </div>

                <div>
                  <span style={{ fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase" }}>
                    Total Stored
                  </span>
                  <p style={{ margin: "2px 0 0", fontSize: "1rem", color: "#38bdf8", fontWeight: 600 }}>
                    {formatNumber(detail.total_leaks ?? detail.health?.total_leaks)}
                  </p>
                </div>

                <div>
                  <span style={{ fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase" }}>
                    24h New Leaks
                  </span>
                  <p style={{ margin: "2px 0 0", fontSize: "1rem", color: "#34d399", fontWeight: 600 }}>
                    {formatNumber(detail.new_leaks_24h ?? detail.health?.new_leaks_24h)}
                  </p>
                </div>

                <div>
                  <span style={{ fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase" }}>
                    Retry Count
                  </span>
                  <p style={{ margin: "2px 0 0", fontSize: "1rem", color: "#cbd5e1", fontWeight: 600 }}>
                    {formatNumber(detail.health?.retry_count)}
                  </p>
                  <span style={{ fontSize: "0.68rem", color: "#64748b" }}>
                    past 100 runs
                  </span>
                </div>

                <div>
                  <span style={{ fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase" }}>
                    Active DLQ
                  </span>
                  <p
                    style={{
                      margin: "2px 0 0",
                      fontSize: "1rem",
                      color: (detail.health?.dlq_count || 0) > 0 ? "#ef4444" : "#10b981",
                      fontWeight: 600,
                    }}
                  >
                    {formatNumber(detail.health?.dlq_count ?? 0)}
                  </p>
                  {detail.health?.historical_dlq !== undefined && detail.health.historical_dlq > 0 && (
                    <span style={{ fontSize: "0.68rem", color: "#64748b" }}>
                      {formatNumber(detail.health.historical_dlq)} past 100 runs
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Recent Runs Timeline Streak */}
            <div className="fmp-card">
              <h3 className="fmp-card-title">
                <Clock size={18} style={{ color: "#06b6d4" }} />
                <span>Recent Execution History Streak</span>
              </h3>
              <p style={{ margin: "0 0 12px 0", fontSize: "0.8rem", color: "#94a3b8" }}>
                Latest 20 scraper attempts. Click any pill to inspect telemetry.
              </p>

              <div className="fmp-streak-bar">
                {detail.runs && detail.runs.length > 0 ? (
                  detail.runs.slice(0, 25).map((r, i) => {
                    const st = (r.status || "").toLowerCase();
                    return (
                      <div
                        key={r.run_id || i}
                        className={`fmp-streak-pill ${st}`}
                        onClick={() => setSelectedRun(r)}
                        title={`Run: ${r.run_id}\nStatus: ${r.status}\nDuration: ${formatDuration(r.duration_seconds)}\nStarted: ${formatDate(r.run_started_at)}`}
                      />
                    );
                  })
                ) : (
                  <span style={{ fontSize: "0.82rem", color: "#64748b" }}>No execution history recorded</span>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Crawler Configuration */}
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <div className="fmp-card">
              <h3 className="fmp-card-title">
                <Server size={18} style={{ color: "#a855f7" }} />
                <span>Crawler Configuration</span>
              </h3>

              <div className="fmp-info-list">
                <div className="fmp-info-item">
                  <span className="fmp-info-label">Network</span>
                  <span className="fmp-info-value">
                    <NetworkBadge network={detail.network} />
                  </span>
                </div>

                <div className="fmp-info-item">
                  <span className="fmp-info-label">Plugin Class</span>
                  <code style={{ fontSize: "0.8rem", color: "#a5b4fc" }}>{detail.plugin}</code>
                </div>

                <div className="fmp-info-item">
                  <span className="fmp-info-label">Scrape Interval</span>
                  <span className="fmp-info-value">
                    {detail.schedule_seconds
                      ? `${Math.round(detail.schedule_seconds / 60)} minutes (${detail.schedule_seconds}s)`
                      : "Default"}
                  </span>
                </div>

                <div className="fmp-info-item">
                  <span className="fmp-info-label">Scraper Enabled</span>
                  <span
                    className="fmp-info-value"
                    style={{ color: detail.enabled ? "#34d399" : "#f87171", fontWeight: 600 }}
                  >
                    {detail.enabled ? "Active" : "Disabled"}
                  </span>
                </div>

                <div className="fmp-info-item">
                  <span className="fmp-info-label">Last Run</span>
                  <span className="fmp-info-value">{formatDate(detail.last_run)}</span>
                </div>

                <div className="fmp-info-item">
                  <span className="fmp-info-label">Last Success</span>
                  <span className="fmp-info-value" style={{ color: "#34d399" }}>
                    {formatDate(detail.last_success)}
                  </span>
                </div>

                <div className="fmp-info-item">
                  <span className="fmp-info-label">Last Failure</span>
                  <span className="fmp-info-value" style={{ color: detail.last_failure ? "#f87171" : "#94a3b8" }}>
                    {formatDate(detail.last_failure)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB 2: RUNS ──────────────────────────────────────────────────── */}
      {tab === "Runs" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Runs Filter Strip */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {["ALL", "SUCCESS", "FAILED", "PARTIAL"].map((st) => (
                <button
                  key={st}
                  type="button"
                  className={`fmp-btn ${runsFilter === st ? "fmp-btn-primary" : ""}`}
                  onClick={() => setRunsFilter(st)}
                  style={{ padding: "6px 14px", fontSize: "0.78rem" }}
                >
                  {st}
                </button>
              ))}
            </div>

            <button type="button" className="fmp-btn" onClick={loadTabData} disabled={runsLoading}>
              <RefreshCw size={13} className={runsLoading ? "animate-spin" : ""} />
              <span>Refresh Runs</span>
            </button>
          </div>

          <div className="fmp-table-card">
            <div className="fmp-table-responsive">
              <table className="fmp-table">
                <thead>
                  <tr>
                    <th>Run ID</th>
                    <th>Status</th>
                    <th>Started</th>
                    <th>Duration</th>
                    <th>Pages</th>
                    <th>Jobs Breakdown</th>
                    <th>New Leaks</th>
                    <th>Trigger</th>
                    <th style={{ textAlign: "right" }}>Telemetry</th>
                  </tr>
                </thead>
                <tbody>
                  {runsLoading ? (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: "36px 0", color: "#94a3b8" }}>
                        <Loader2 size={18} className="animate-spin text-indigo-400" />
                      </td>
                    </tr>
                  ) : filteredRuns.length > 0 ? (
                    filteredRuns.map((r) => (
                      <tr key={r.run_id}>
                        <td>
                          <code style={{ fontSize: "0.8rem", color: "#a5b4fc" }}>{r.run_id}</code>
                          {r.failure_message && (
                            <p style={{ margin: "4px 0 0", fontSize: "0.74rem", color: "#f87171", maxWidth: 300, whiteSpace: "normal" }}>
                              {r.failure_message}
                            </p>
                          )}
                        </td>

                        <td>
                          <ForumStatusBadge status={r.status || r.run_status || "UNKNOWN"} />
                        </td>

                        <td>
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <span>{formatDate(r.run_started_at)}</span>
                            <span style={{ fontSize: "0.72rem", color: "#64748b" }}>{timeAgo(r.run_started_at)}</span>
                          </div>
                        </td>

                        <td>
                          <span className="fmp-pill-metric">{formatDuration(r.duration_seconds)}</span>
                        </td>

                        <td>{formatNumber(r.pages_discovered || r.pages_processed)}</td>

                        <td>
                          <span style={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
                            {formatNumber(r.jobs_succeeded)} / {formatNumber(r.jobs_queued)}
                            {(r.jobs_failed || 0) > 0 && (
                              <span style={{ color: "#f87171", marginLeft: 4 }}>
                                ({r.jobs_failed} failed)
                              </span>
                            )}
                          </span>
                        </td>

                        <td>
                          <span className={`fmp-pill-metric ${(r.new_records || 0) > 0 ? "has-value" : ""}`}>
                            {formatNumber(r.new_records)}
                          </span>
                        </td>

                        <td>
                          <span style={{ textTransform: "capitalize", fontSize: "0.8rem", color: "#94a3b8" }}>
                            {r.triggered_by || "scheduler"}
                          </span>
                        </td>

                        <td style={{ textAlign: "right" }}>
                          <button
                            type="button"
                            className="fmp-action-btn"
                            onClick={() => setSelectedRun(r)}
                          >
                            <Terminal size={12} />
                            <span>Inspect</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} style={{ textAlign: "center", padding: "36px 0", color: "#94a3b8" }}>
                        No runs recorded matching the filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB 3: ERRORS & DIAGNOSTICS ──────────────────────────────────── */}
      {tab === "Errors" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Formal Diagnostic Errors from monitoring_errors */}
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "1.05rem", color: "#ffffff", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
                <AlertTriangle size={18} style={{ color: "#ef4444" }} />
                <span>Diagnostic Error Incident Logs</span>
              </h3>
              <button type="button" className="fmp-btn" onClick={loadTabData} disabled={errorsLoading}>
                <RefreshCw size={13} className={errorsLoading ? "animate-spin" : ""} />
                <span>Refresh Diagnostics</span>
              </button>
            </div>

            {errorsLoading ? (
              <div className="fmp-card" style={{ textAlign: "center", padding: "36px 0", color: "#94a3b8" }}>
                <Loader2 size={18} className="animate-spin text-indigo-400" />
              </div>
            ) : errors.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {errors.map((err) => (
                  <div key={err.error_id} className="fmp-card" style={{ padding: "16px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                          <span style={{ fontWeight: 700, color: "#f87171", fontSize: "0.92rem" }}>
                            {err.error_type}
                          </span>
                          <span
                            className="fmp-badge"
                            style={{
                              background: err.resolution_status === "RESOLVED" ? "rgba(16, 185, 129, 0.15)" : "rgba(245, 158, 11, 0.15)",
                              color: err.resolution_status === "RESOLVED" ? "#34d399" : "#f59e0b",
                            }}
                          >
                            {err.resolution_status || "OPEN"}
                          </span>
                        </div>
                        <p style={{ margin: "0 0 6px", color: "#e2e8f0", fontSize: "0.85rem" }}>{err.message}</p>
                        <span style={{ fontSize: "0.74rem", color: "#64748b" }}>
                          ID: {err.error_id} • Last seen: {formatDate(err.last_seen)}
                        </span>
                      </div>

                      <button
                        type="button"
                        className="fmp-btn fmp-btn-primary"
                        onClick={() => setSelectedError(err)}
                        style={{ padding: "6px 14px" }}
                      >
                        <Terminal size={13} />
                        <span>Inspect Diagnostics</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="fmp-card"
                style={{
                  padding: "24px",
                  textAlign: "center",
                  color: "#94a3b8",
                  background: "rgba(16, 185, 129, 0.03)",
                  borderColor: "rgba(16, 185, 129, 0.15)",
                }}
              >
                <CheckCircle2 size={24} style={{ color: "#10b981", margin: "0 auto 8px" }} />
                <p style={{ margin: 0, fontWeight: 500, color: "#f8fafc" }}>
                  No active diagnostic incidents logged for this forum
                </p>
                <p style={{ margin: "4px 0 0", fontSize: "0.8rem", color: "#64748b" }}>
                  All scraper runs are within expected error tolerances.
                </p>
              </div>
            )}
          </div>

          {/* Scraper Run Failures (from monitoring_runs telemetry) */}
          {failedRunsFromHistory.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <h3 style={{ margin: "0 0 12px 0", fontSize: "1.05rem", color: "#ffffff", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
                <ShieldAlert size={18} style={{ color: "#f59e0b" }} />
                <span>Recent Scraper Run Failures & Parser Errors ({failedRunsFromHistory.length})</span>
              </h3>

              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {failedRunsFromHistory.map((fr) => (
                  <div key={fr.run_id} className="fmp-card" style={{ padding: "14px 18px", borderColor: "rgba(239, 68, 68, 0.2)" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                          <span style={{ fontWeight: 600, color: "#f87171", fontSize: "0.88rem" }}>
                            {fr.failure_type || "scraper_failure"}
                          </span>
                          <span style={{ fontSize: "0.75rem", fontFamily: "monospace", color: "#a5b4fc" }}>
                            {fr.run_id}
                          </span>
                        </div>
                        <p style={{ margin: "0 0 6px 0", color: "#fee2e2", fontSize: "0.84rem", lineHeight: 1.4 }}>
                          {fr.failure_message || "Scraper execution failed to complete successfully."}
                        </p>
                        <span style={{ fontSize: "0.74rem", color: "#64748b" }}>
                          Occurred: {formatDate(fr.run_started_at)} ({timeAgo(fr.run_started_at)}) • Duration: {formatDuration(fr.duration_seconds)}
                        </span>
                      </div>

                      <button
                        type="button"
                        className="fmp-btn"
                        onClick={() => setSelectedRun(fr)}
                        style={{ padding: "5px 12px", fontSize: "0.76rem" }}
                      >
                        <Terminal size={12} />
                        <span>Telemetry</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── TAB 4: OPERATIONAL NOTES ─────────────────────────────────────── */}
      {tab === "Notes" && (
        <div>
          {/* Notes Composer */}
          <form className="fmp-notes-composer" onSubmit={handleAddNote}>
            <h3 style={{ margin: 0, fontSize: "0.95rem", color: "#ffffff", fontWeight: 600, display: "flex", alignItems: "center", gap: "8px" }}>
              <Edit3 size={15} style={{ color: "#818cf8" }} />
              <span>Add Operational / Handover Note</span>
            </h3>

            <textarea
              className="fmp-notes-textarea"
              placeholder="Record operational notes, CAPTCHA changes, login credentials, or investigation findings..."
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              required
            />

            <div className="fmp-notes-actions">
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "0.82rem", color: "#cbd5e1", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={pinNewNote}
                  onChange={(e) => setPinNewNote(e.target.checked)}
                />
                <Pin size={13} style={{ color: pinNewNote ? "#f59e0b" : "#94a3b8" }} />
                <span>Pin note to top</span>
              </label>

              <button
                type="submit"
                className="fmp-btn fmp-btn-primary"
                disabled={submittingNote || !newNoteText.trim()}
              >
                {submittingNote ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                <span>Post Note</span>
              </button>
            </div>
          </form>

          {/* Notes List */}
          <div>
            {notes.length > 0 ? (
              notes.map((n) => (
                <div key={n._id} className={`fmp-note-card ${n.pinned ? "pinned" : ""}`}>
                  <div className="fmp-note-header">
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      {n.pinned && (
                        <span style={{ display: "flex", alignItems: "center", gap: "4px", color: "#f59e0b", fontWeight: 600, fontSize: "0.76rem" }}>
                          <Pin size={13} />
                          <span>PINNED</span>
                        </span>
                      )}
                      <span>Author: {n.author?.email || "Admin"}</span>
                      <span>•</span>
                      <span>{formatDate(n.created_at)}</span>
                      {n.updated_at && n.updated_at !== n.created_at && (
                        <span style={{ color: "#64748b" }}>(edited)</span>
                      )}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <button
                        type="button"
                        className="fmp-action-btn"
                        onClick={() => handleTogglePinNote(n)}
                        title={n.pinned ? "Unpin note" : "Pin note"}
                      >
                        <Pin size={12} style={{ color: n.pinned ? "#f59e0b" : "#94a3b8" }} />
                        <span>{n.pinned ? "Unpin" : "Pin"}</span>
                      </button>

                      <button
                        type="button"
                        className="fmp-action-btn"
                        onClick={() => handleEditNote(n)}
                        title="Edit note text"
                      >
                        <Edit3 size={12} />
                        <span>Edit</span>
                      </button>

                      <button
                        type="button"
                        className="fmp-action-btn danger"
                        onClick={() => handleDeleteNote(n._id)}
                        title="Delete note"
                      >
                        <Trash2 size={12} />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>

                  <p className="fmp-note-text">{n.text}</p>
                </div>
              ))
            ) : (
              <div className="fmp-card" style={{ padding: "36px", textAlign: "center", color: "#94a3b8" }}>
                <FileText size={24} style={{ color: "#64748b", margin: "0 auto 8px" }} />
                <p style={{ margin: 0 }}>No operational notes recorded for {forum}</p>
                <p style={{ margin: "4px 0 0", fontSize: "0.8rem", color: "#64748b" }}>
                  Use the composer above to log shift updates, incident notes, or crawler tweaks.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── TAB 5: ACTIVITY STREAM ───────────────────────────────────────── */}
      {tab === "Activity" && (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {["ALL", "RUN", "ERROR", "NOTE", "ACTION", "AUDIT"].map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`fmp-btn ${activityFilter === f ? "fmp-btn-primary" : ""}`}
                  onClick={() => setActivityFilter(f)}
                  style={{ padding: "5px 12px", fontSize: "0.76rem" }}
                >
                  {f}
                </button>
              ))}
            </div>

            <button type="button" className="fmp-btn" onClick={loadTabData}>
              <RefreshCw size={13} />
              <span>Refresh Activity</span>
            </button>
          </div>

          <div className="fmp-timeline">
            {filteredActivities.length > 0 ? (
              filteredActivities.map((act, i) => (
                <div key={act.request_id || act.run_id || act.error_id || act.note_id || i} className="fmp-timeline-item">
                  <span className="fmp-timeline-dot" />
                  <div className="fmp-timeline-content">
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                        <span style={{ fontSize: "0.78rem", fontWeight: 700, textTransform: "uppercase", color: "#a5b4fc" }}>
                          {act.type}
                        </span>
                        {act.status && <ForumStatusBadge status={act.status} />}
                        {act.action && (
                          <span style={{ fontSize: "0.76rem", color: "#cbd5e1", fontWeight: 600 }}>
                            {act.action}
                          </span>
                        )}
                      </div>
                      <p style={{ margin: "0 0 4px", fontSize: "0.84rem", color: "#e2e8f0" }}>
                        {act.message || act.run_id || act.action || "Operational event recorded"}
                      </p>
                    </div>

                    <span style={{ fontSize: "0.74rem", color: "#64748b", whiteSpace: "nowrap" }}>
                      {formatDate(act.timestamp)} ({timeAgo(act.timestamp)})
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="fmp-card" style={{ padding: "36px", textAlign: "center", color: "#94a3b8" }}>
                <Clock size={24} style={{ color: "#64748b", margin: "0 auto 8px" }} />
                <p style={{ margin: 0 }}>No activity events found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Modals ───────────────────────────────────────────────────────── */}
      <RunTelemetryModal run={selectedRun} onClose={() => setSelectedRun(null)} />

      <DiagnosticErrorModal
        error={selectedError}
        onClose={() => setSelectedError(null)}
        onResolve={handleResolveError}
      />
    </main>
  );
}
